import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { mdiCheck, mdiClose, mdiCloseCircle, mdiDownload } from '@mdi/js';
import { useApolloClient } from '@apollo/client/react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';

import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { Divider } from 'common/components/Divider';
import {
    ICertificateValidationStatus,
    IReceivedCertificateFragment,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { ReceivedCertificateDatesForm } from 'common/forms';
import { Loader } from 'common/components/Loader';
import { formatDate } from 'common/utils/formatDate';
import { Chip } from 'common/components/Chip';
import { useSnackbar } from 'common/hooks/useSnackbar';

// Set worker source to local package worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface IProps {
    canUpdate: boolean;
    open: boolean;
    receivedCertificate?: IReceivedCertificateFragment;
    fileUrl?: string;
    title?: string;
    onClose: () => void;
    onSetExternalStatus?: (isApproved: boolean) => Promise<void>;
}

export const ReceivedCertificatePreviewModal = ({
    canUpdate,
    open,
    receivedCertificate,
    fileUrl,
    title,
    onClose,
    onSetExternalStatus,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const client = useApolloClient();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const handleDownload = useCallback(() => {
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        }
    }, [fileUrl]);

    const handleSetExternalStatus = useCallback(
        async (isApproved: boolean) => {
            if (!receivedCertificate) return;

            try {
                // Call the parent handler (from list item) to execute mutation and cache evictions
                await onSetExternalStatus?.(isApproved);
            } catch (error) {
                showApolloError(error);

                return;
            }

            // Update the cache directly to keep the modal open and reflect changes immediately
            const now = new Date().toISOString();
            client.cache.modify({
                id: client.cache.identify(receivedCertificate),
                fields: {
                    validationStatus() {
                        return isApproved
                            ? ICertificateValidationStatus.Approved
                            : ICertificateValidationStatus.Denied;
                    },
                    validationDate() {
                        return now;
                    },
                },
            });
        },
        [receivedCertificate, onSetExternalStatus, showApolloError, client]
    );

    const pdfLoadErrorText = translate('receivedCertificate.pdfLoadError');

    useEffect(() => {
        if (!open) return;

        if (!fileUrl) {
            setIsLoading(false);
            setLoadError(null);

            return;
        }

        let isCancelled = false;
        let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
        let renderTask: pdfjsLib.RenderTask | null = null;
        // requestAnimationFrameId
        let rafId: number | null = null;

        const loadPdf = async () => {
            if (isCancelled) return;

            setIsLoading(true);
            setLoadError(null);

            // Wait for canvas to be available in DOM
            await new Promise((resolve) => {
                rafId = requestAnimationFrame(resolve);
            });

            const canvas = canvasRef.current;

            if (!canvas) {
                setLoadError(pdfLoadErrorText);
                setIsLoading(false);

                return;
            }

            const context = canvas.getContext('2d');

            if (!context) {
                setLoadError(pdfLoadErrorText);
                setIsLoading(false);

                return;
            }

            try {
                loadingTask = pdfjsLib.getDocument(fileUrl);
                const pdf = await loadingTask.promise;

                const page = await pdf.getPage(1);

                // Get the container width to calculate the scale
                const container = canvas.parentElement;
                const containerWidth = container?.clientWidth || 800;

                // Get the viewport at scale 1.0 to get original dimensions
                const unscaledViewport = page.getViewport({ scale: 1.0 });

                // Calculate scale to fit the container width
                const scale = containerWidth / unscaledViewport.width;

                // Get the viewport with the calculated scale
                const viewport = page.getViewport({ scale });

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                renderTask = page.render({
                    canvasContext: context,
                    viewport: viewport,
                    canvas: canvas,
                });

                await renderTask.promise;

                setIsLoading(false);
            } catch {
                // Ignore errors if the component was unmounted or task was cancelled

                setLoadError(pdfLoadErrorText);
                setIsLoading(false);
            }
        };

        loadPdf();

        return () => {
            isCancelled = true;

            // Cancel the render task if it's in progress
            if (renderTask) renderTask.cancel();

            // Cancel the PDF loading task if it's in progress
            if (loadingTask) loadingTask.destroy();

            // Cancel the requestAnimationFrame if it hasn't executed yet
            if (rafId !== null) cancelAnimationFrame(rafId);
        };
    }, [open, fileUrl, pdfLoadErrorText]);

    // Display snackbar error when there is an error loading the pdf
    useEffect(() => {
        if (loadError && open) {
            displaySnackbar(translate('receivedCertificate.pdfLoadError'), {
                variant: 'error',
            });
        }
    }, [loadError, displaySnackbar, translate, open]);

    if (!open) return null;

    const {
        isExternalCertificate,
        requiresValidation,
        validationStatus,
        validationDate,
        isExpired,
        expiryDate,
    } = receivedCertificate || {};

    const isApproved =
        !!validationStatus &&
        validationStatus === ICertificateValidationStatus.Approved;
    const isDenied =
        !!validationStatus &&
        validationStatus === ICertificateValidationStatus.Denied;
    const isPending =
        !!validationStatus &&
        validationStatus === ICertificateValidationStatus.Pending;

    // Is either approved or denied by a managing user, a manual status is given
    const hasManualStatus = isApproved || isDenied;

    // Show status text instead of buttons when expired, approved, or denied
    const hasResolvedStatus = hasManualStatus || isExpired;

    const statusTextKey = isExpired
        ? 'receivedCertificate.expiredOn'
        : isApproved
          ? 'receivedCertificate.approvedOn'
          : isDenied
            ? 'receivedCertificate.deniedOn'
            : 'receivedCertificate.pending';

    const statusTextParams = isExpired
        ? { date: formatDate(expiryDate, 'd MMMM yyyy') }
        : hasManualStatus
          ? { date: formatDate(validationDate, 'd MMMM yyyy') }
          : undefined;

    return (
        <AlertDialog
            fullWidth
            actions={
                <Box sx={{ width: '100%' }}>
                    <Divider sx={{ my: 3 }} />

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            width: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {!!fileUrl && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={handleDownload}
                                >
                                    <Box
                                        sx={{
                                            display: {
                                                xs: 'flex',
                                                sm: 'none',
                                            },
                                        }}
                                    >
                                        <Icon path={mdiDownload} size="2rem" />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: {
                                                xs: 'none',
                                                sm: 'block',
                                            },
                                        }}
                                    >
                                        {translate('download')}
                                    </Box>
                                </Button>
                            )}

                            <Button variant="contained" onClick={onClose}>
                                <Box
                                    sx={{
                                        display: {
                                            xs: 'flex',
                                            sm: 'none',
                                        },
                                    }}
                                >
                                    <Icon path={mdiCloseCircle} size="2rem" />
                                </Box>
                                <Box
                                    sx={{
                                        display: {
                                            xs: 'none',
                                            sm: 'block',
                                        },
                                    }}
                                >
                                    {translate('close')}
                                </Box>
                            </Button>
                        </Box>

                        {isExternalCertificate &&
                            requiresValidation &&
                            validationStatus && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        width: {
                                            xs:
                                                hasResolvedStatus || !canUpdate
                                                    ? '100%'
                                                    : 'auto',
                                            md: 'auto',
                                        },
                                        ml: { md: 'auto' },
                                    }}
                                >
                                    {(hasResolvedStatus || !canUpdate) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'center',
                                                mt: { xs: 2, md: 0 },
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {translate(
                                                    statusTextKey,
                                                    statusTextParams
                                                )}
                                            </Typography>

                                            <Chip
                                                sx={{
                                                    backgroundColor: isExpired
                                                        ? 'error.main'
                                                        : isApproved
                                                          ? 'success.main'
                                                          : isPending
                                                            ? 'validation.main'
                                                            : 'error.main',
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {!hasResolvedStatus && canUpdate && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                            }}
                                        >
                                            <Button
                                                color="success"
                                                variant="contained"
                                                onClick={() =>
                                                    handleSetExternalStatus(
                                                        true
                                                    )
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        display: {
                                                            xs: 'flex',
                                                            sm: 'none',
                                                        },
                                                    }}
                                                >
                                                    <Icon
                                                        path={mdiCheck}
                                                        size="2rem"
                                                    />
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: {
                                                            xs: 'none',
                                                            sm: 'block',
                                                        },
                                                    }}
                                                >
                                                    {translate(
                                                        'receivedCertificate.approve'
                                                    )}
                                                </Box>
                                            </Button>

                                            <Button
                                                color="error"
                                                variant="contained"
                                                onClick={() =>
                                                    handleSetExternalStatus(
                                                        false
                                                    )
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        display: {
                                                            xs: 'flex',
                                                            sm: 'none',
                                                        },
                                                    }}
                                                >
                                                    <Icon
                                                        path={mdiClose}
                                                        size="2rem"
                                                    />
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: {
                                                            xs: 'none',
                                                            sm: 'block',
                                                        },
                                                    }}
                                                >
                                                    {translate(
                                                        'receivedCertificate.reject'
                                                    )}
                                                </Box>
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                    </Box>
                </Box>
            }
            maxWidth="lg"
            open={open}
            title={title || ''}
            onClose={onClose}
        >
            {canUpdate &&
                isExternalCertificate &&
                requiresValidation &&
                receivedCertificate && (
                    <>
                        <ReceivedCertificateDatesForm
                            receivedCertificate={receivedCertificate}
                        />

                        <Divider sx={{ my: 3 }} />
                    </>
                )}

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    width: '100%',
                }}
            >
                {isLoading && <Loader />}

                {loadError && !isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            color: 'error.main',
                        }}
                    >
                        <Typography variant="h6">{pdfLoadErrorText}</Typography>
                    </Box>
                )}

                {!loadError && (
                    <canvas
                        aria-label={translate(
                            'receivedCertificate.certificatePreview'
                        )}
                        ref={canvasRef}
                        role="img"
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            display: isLoading ? 'none' : 'block',
                        }}
                    />
                )}
            </Box>
        </AlertDialog>
    );
};
